import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { asId, type DlpRefusal, type SendMessageRequest, type Uuid } from '@cyphex/types';
import { chatApi, ChatApiError, type ConnectionState } from '../api';
import { CHAT_KEYS, OUTBOX_BACKOFF_BASE_MS, OUTBOX_MAX_ATTEMPTS } from '../constants';
import { bumpConversation } from '../lib/conversationCache';
import { nextBackoff } from '../lib/optimistic';
import { appendMessage } from '../lib/timelineCache';
import { outboxStore } from '../store/outboxStore';
import type { OutboxItem } from '../types';

const TICK_MS = 2_000;

function toRequest(item: OutboxItem): SendMessageRequest {
  return {
    clientMsgId: asId<Uuid>(item.clientMsgId),
    text: item.text,
    ...(item.body ? { body: item.body } : {}),
    ...(item.mentions.length > 0 ? { mentions: item.mentions } : {}),
    ...(item.fileIds.length > 0 ? { fileIds: item.fileIds } : {}),
    ...(item.replyToId ? { replyToId: item.replyToId } : {}),
    ...(item.threadRootId ? { threadRootId: item.threadRootId } : {}),
    ...(item.scheduledFor ? { scheduledFor: item.scheduledFor } : {}),
    ...(item.voice ? { voice: item.voice } : {}),
    ...(item.dlpAcknowledged ? { dlpAcknowledged: true } : {}),
  };
}

/**
 * A data-protection WARNING, which is a refusal the sender can overrule — not
 * an error and not something to retry on a timer. Returns the refusal so the
 * bubble can offer "Send anyway", or null for every other failure.
 */
function dlpWarning(error: unknown): DlpRefusal | null {
  if (!(error instanceof ChatApiError) || error.status !== 409) return null;
  const details = error.details as DlpRefusal | null;
  return details !== null && details.action === 'warn' && details.acknowledgeable
    ? details
    : null;
}

/**
 * Drains the durable outbox. Runs on a slow tick and on every reconnect —
 * never a tight loop. Because `clientMsgId` is the server idempotency key, a
 * replay after a lost response returns the original message instead of a
 * duplicate, which is what makes offline-first safe.
 */
export function useOutboxFlush(connection: ConnectionState): void {
  const client = useQueryClient();
  const busy = useRef(false);

  useEffect(() => {
    let cancelled = false;

    const flush = async (): Promise<void> => {
      if (busy.current || !navigator.onLine) return;
      busy.current = true;
      try {
        for (const item of outboxStore.due(Date.now())) {
          if (cancelled) break;
          await outboxStore.update(item.clientMsgId, { state: 'sending' });
          try {
            const message = await chatApi.sendMessage(item.conversationId, toRequest(item));
            // A SEND-LATER IS NOT A SEND. The server answers 201 with a
            // `state: 'scheduled'` row that it has NOT delivered to anyone, so
            // dropping it into the timeline (and bumping the chat list with it)
            // showed the author a message everyone else could see — hours
            // before it existed for them. It belongs in the scheduled queue
            // until the dispatcher promotes it.
            if (item.scheduledFor) {
              void client.invalidateQueries({
                queryKey: CHAT_KEYS.scheduled(item.conversationId),
              });
            } else {
              appendMessage(client, item.conversationId, message, item.clientMsgId);
              bumpConversation(client, item.conversationId, message, true);
            }
            await outboxStore.remove(item.clientMsgId);
          } catch (error) {
            const retryable = !(error instanceof ChatApiError) || error.retryable;
            const attempts = item.attempts + 1;
            const dead = !retryable || attempts >= OUTBOX_MAX_ATTEMPTS;
            await outboxStore.update(item.clientMsgId, {
              attempts,
              state: dead ? 'failed' : 'queued',
              nextAttemptAt: dead ? Number.MAX_SAFE_INTEGER : nextBackoff(attempts, OUTBOX_BACKOFF_BASE_MS),
              lastError: error instanceof Error ? error.message : 'Send failed',
              // Carries the refusal onto the bubble so the sender is offered
              // "Send anyway" instead of a bare "Not sent" they cannot act on.
              dlpRefusal: dlpWarning(error),
            });
          }
        }
      } finally {
        busy.current = false;
      }
    };

    const onOnline = (): void => void flush();
    void outboxStore.hydrate().then(flush);
    const timer = setInterval(onOnline, TICK_MS);
    window.addEventListener('online', onOnline);
    return () => {
      cancelled = true;
      clearInterval(timer);
      window.removeEventListener('online', onOnline);
    };
  }, [client, connection]);
}
